import { SortDirection } from '@tanstack/react-table';

import { Flex, Icon, MdSouth, Text } from '@oaknorth/component-core-v2';
import { useTranslation } from '@oaknorth/util-intl';

interface SortIndicatorProps {
  /**
   * Whether the column is currently sorted
   */
  isSorted: boolean;

  /**
   * If the column is currently sorted, is it sorted in descending order?
   */
  isSortedDesc?: boolean;
  /**
   * Which direction the column will be sorted on first sorting
   */
  firstSortDirection: SortDirection;
  /**
   * Whether to indicate the sort priority of the current column next to the sort icon
   */
  showSortPriority: boolean;
  /**
   * The sort index of the current column, which represents the sort priority
   */
  sortIndex: number;
  /**
   * Whether the data displayed in column that the sort indicator is in is numeric.
   * Determines which side of the icon the sort priority indicator will be rendered
   */
  isNumeric?: boolean;
  /**
   * The id of the column the sort indicator is in
   */
  columnId: string;
}

function SortIndexIndicator({ sortIndex, 'data-testid': testId }: { sortIndex: number; 'data-testid'?: string }) {
  return (
    <Text as="sup" fontSize="xs" data-testid={testId}>
      {sortIndex + 1}
    </Text>
  );
}
export function SortIndicator({
  isSorted,
  isSortedDesc = false,
  firstSortDirection,
  showSortPriority,
  sortIndex,
  isNumeric,
  columnId,
}: SortIndicatorProps) {
  const { t } = useTranslation();
  const showDescendingIcon = isSortedDesc || (!isSorted && firstSortDirection === 'desc');

  return (
    <Flex alignItems="center" title={t`table.column.sortIcon`}>
      {showSortPriority && isNumeric && (
        <SortIndexIndicator sortIndex={sortIndex} data-testid={`column-${columnId}-sort-index-indicator-left`} />
      )}
      <Icon
        as={MdSouth}
        h={4}
        w={4}
        transform={showDescendingIcon ? undefined : 'rotate(-180deg)'}
        transition="opacity ease-in 100ms, transform ease-in 200ms"
        flexShrink={0}
        display="inline-block"
        visibility={isSorted ? 'visible' : 'hidden'}
        opacity={isSorted ? 1 : 0}
        _groupHover={{ opacity: isSorted ? 1 : 0.7 }}
        data-testid={`column-${columnId}-sort-indicator`}
      />
      {showSortPriority && !isNumeric && (
        <SortIndexIndicator sortIndex={sortIndex} data-testid={`column-${columnId}-sort-index-indicator-right`} />
      )}
    </Flex>
  );
}
